import React, { useCallback, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';
import { Box, Chip } from '@mui/material';
import { IconProp } from '@fortawesome/fontawesome-svg-core';

import {
    ContextActionType,
    EmptyBox,
    HeaderActionButton,
    HeaderListSkeleton,
    ListHeader,
    ListSkeleton,
    SkeletonLoader,
    useDumbActionsContext,
    useModal,
} from '@europrocurement/flexy-components';
import {
    ColumnDatatable,
    FiltersDatatableList,
    StoreDatatable,
} from '@europrocurement/flexy-datatable';
import {
    antennesSelector,
    AntennesUtilisateurApiObject,
    useUtilisateurService,
    useUtilisateursServiceSelector,
    UtilisateursAppDispatch,
} from '@europrocurement/l2d-domain';
import { customizerSelector } from '@europrocurement/flexy-components/redux/storeConfig/selectors';
import {
    errorNotification,
    successNotification,
} from '@europrocurement/flexy-components/components/atoms/NotificationToasters';
import { FlexyForm } from '@europrocurement/flexy-form';
import { FaOptionIcon, plusIcon } from '@europrocurement/l2d-icons';

import { useInitialLoadingState } from '@europrocurement/l2d-hooks';
import { useAppSelector } from '../../../../redux';
import { getAntennaCreationFormStructure } from '../../components/Antenne/antennaFormStructure';

export type AntennaType = {
    idAntenne?: number;
    libelle?: string | null;
    isDeleted: boolean;
};

type AntennaCreationType = {
    libelle?: string | null;
};

const filters: FiltersDatatableList = [
    {
        label: 'Supprimé',
        field: 'isDeleted',
        type: 'boolean',
    },
];

const defaultCreatedAntennaValues: AntennaCreationType = {
    libelle: '',
};

/**
 * Page de listing des antennes.
 * Affiche les antennes dans un StoreDatatable avec navigation vers la fiche détail.
 */
export const AntennesList: React.FunctionComponent = function () {
    const navigate = useNavigate();
    const dispatch = useDispatch<UtilisateursAppDispatch>();
    const { modalActions } = useModal();

    const { xIdSociete } = useAppSelector(customizerSelector);
    const { getAntennes, antennesApi } = useUtilisateurService();

    const dataSource = useUtilisateursServiceSelector(antennesSelector);
    const { isInitialLoading, isInError } = useInitialLoadingState(dataSource);

    const breadcrumbPath = useMemo(
        () => [
            {
                link: '/accueil',
                icon: {
                    icon: ['fasl', 'house'] as IconProp,
                    sx: { display: 'flex', alignItems: 'center' },
                },
            },
            { link: '/utilisateurs/antennes', path: 'Antennes' },
            'Portail Antennes',
        ],
        [],
    );

    const columns: ColumnDatatable<AntennesUtilisateurApiObject>[] = useMemo(
        () => [
            {
                label: 'ID',
                render: 'idAntenne',
                isDisplayed: true,
                sortable: true,
                field: 'idAntenne',
                sx: { width: '100px' },
            },
            {
                label: 'Libelle',
                render: 'libelle',
                isDisplayed: true,
                sortable: true,
                field: 'libelle',
            },
            {
                label: 'Statut',
                isDisplayed: true,
                field: 'isDeleted',
                render: (antenne: AntennesUtilisateurApiObject) =>
                    antenne.isDeleted ? (
                        <Chip label="Inactif" />
                    ) : (
                        <Chip
                            label="Actif"
                            color="primary"
                        />
                    ),
            },
        ],
        [],
    );

    const handleCreateAntenna = useCallback(
        (formValues: AntennaType) => {
            const payload: AntennaCreationType = {
                libelle: formValues.libelle,
            };

            antennesApi
                .apiAntennesPost({ antennesJsonldAntenneWrite: payload })
                .then(() => {
                    successNotification("L'antenne a bien été créée.");
                    dispatch(getAntennes({}));
                    modalActions.close();
                })
                .catch(errorNotification);
        },
        [antennesApi, dispatch, getAntennes, modalActions],
    );

    const openCreateAntennaForm = useCallback(() => {
        modalActions.call(
            <FlexyForm
                formObject={defaultCreatedAntennaValues}
                formStructure={getAntennaCreationFormStructure()}
                onSubmit={handleCreateAntenna}
            />,
        );
    }, [handleCreateAntenna, modalActions]);

    // Chargement initial des données
    useEffect(() => {
        dispatch(getAntennes({}));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleRowClick = useCallback(
        (_: React.MouseEvent<HTMLElement>, antenne: AntennesUtilisateurApiObject) => {
            navigate(`${antenne.idAntenne}`);
        },
        [navigate],
    );

    const contextActions: ContextActionType[] = useMemo(
        () => [
            {
                name: 'Nouvelle antenne',
                icon: <FaOptionIcon {...plusIcon.props} />,
                action: openCreateAntennaForm,
            },
        ],
        [openCreateAntennaForm],
    );

    useDumbActionsContext(contextActions);

    const errorContent = useMemo(
        () => (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100vh',
                    width: '100%',
                }}
            >
                <EmptyBox
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'space-evenly',
                        height: '25%',
                        width: '50%',
                    }}
                    text="Une erreur s'est produite lors de la récupération des antennes."
                    subText="Veuillez recharger la page. Si le problème persiste, contactez votre administrateur."
                />
            </Box>
        ),
        [],
    );

    return (
        <SkeletonLoader
            isLoading={isInitialLoading}
            skeleton={
                <>
                    <HeaderListSkeleton />
                    <ListSkeleton
                        showSearch
                        columns={3}
                    />
                </>
            }
            isInError={isInError}
            errorChildren={errorContent}
        >
            <ListHeader
                breadcrumbPath={breadcrumbPath}
                actions={
                    <HeaderActionButton
                        label="Nouvelle antenne"
                        icon={plusIcon}
                        onClick={openCreateAntennaForm}
                        actionType="modal"
                    />
                }
            />

            {dataSource && (
                <StoreDatatable
                    dataSource={dataSource}
                    columns={columns}
                    fetchData={getAntennes}
                    filters={filters}
                    onClickRow={handleRowClick}
                    observers={[xIdSociete]}
                />
            )}
        </SkeletonLoader>
    );
};
